import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "motion/react";
import { Loader2, Search } from "lucide-react";
import { PortfolioItem } from "../types";
import { apiFetch } from "../lib/apiFetch";

export const TickerEditor = React.memo(
  ({
    item,
    isSelected,
    onUpdateTicker,
    className,
  }: {
    item: PortfolioItem;
    isSelected: boolean;
    onUpdateTicker: (id: string, newTicker: string) => void;
    className?: string;
  }) => {
    const { t } = useTranslation();
    const [val, setVal] = useState(item.ticker);
    const [results, setResults] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [show, setShow] = useState(false);
    const wrapperRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
      setVal(item.ticker);
    }, [item.ticker]);

    useEffect(() => {
      const handleClick = (e: MouseEvent) => {
        if (
          wrapperRef.current &&
          !wrapperRef.current.contains(e.target as Node)
        ) {
          setShow(false);
          setVal(item.ticker);
        }
      };
      document.addEventListener("mousedown", handleClick);
      return () => document.removeEventListener("mousedown", handleClick);
    }, [item.ticker]);

    useEffect(() => {
      const handler = setTimeout(() => {
        if (val.length >= 3 && val !== item.ticker) {
          search(val);
        } else if (val.length < 3) {
          setResults([]);
        }
      }, 500);
      return () => clearTimeout(handler);
    }, [val, item.ticker]);

    const search = async (q: string) => {
      if (!q) {
        setResults([]);
        return;
      }
      setLoading(true);
      try {
        const r = await apiFetch(`/api/search?q=${encodeURIComponent(q)}`);
        const d = await r.json();
        setResults(d.results || []);
        setShow(true);
      } catch (e) { }
      setLoading(false);
    };

    return (
      <div className="relative" ref={wrapperRef}>
        <div className="flex items-center">
          <input
            name="ticker-editor"
            aria-label="Stock ticker symbol"
            value={val}
            autoFocus={item.ticker === ""}
            onChange={(e) => setVal(e.target.value.toUpperCase())}
            onKeyDown={(e) => {
              if (e.key === "Enter") search(val);
            }}
            className={
              className ||
              `input-underlined w-full font-black tracking-tight text-base ${isSelected ? "text-emerald-400" : "text-zinc-200"}`
            }
          />
          {loading ? (
            <Loader2 size={12} className="animate-spin opacity-50 ml-1" />
          ) : (
            <Search
              size={16}
              className="opacity-60 ml-1 cursor-pointer hover:opacity-100 transition-opacity text-zinc-300 hover:text-emerald-400"
              onClick={() => search(val)}
            />
          )}
        </div>
        <AnimatePresence>
          {show && results.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              className="absolute top-full left-0 mt-1 w-64 card-panel z-50 max-h-64 overflow-y-auto"
            >
              {results.map((r, i) => (
                <div
                  key={i}
                  onClick={() => {
                    setShow(false);
                    setVal(r.symbol);
                    onUpdateTicker(item.id!, r.symbol);
                  }}
                  className="p-2 border-b border-zinc-800/50 hover:bg-zinc-800 cursor-pointer flex justify-between items-center transition-colors"
                >
                  <div>
                    <p className="text-mono-sm font-bold">
                      {r.symbol}{" "}
                      <span className="text-[9px] text-zinc-300 font-sans tracking-widest">
                        {r.exchDisp || r.exchange}
                      </span>
                    </p>
                    <p className="text-zinc-300 text-[10px] truncate max-w-[150px]">
                      {r.longname || r.shortname}
                    </p>
                  </div>
                  <span className="badge-primary px-2 py-0.5">
                    {t("common.select")}
                  </span>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  },
);
